import axios from 'axios';
import { logError, withRetry } from './errorBoundary';
import { setupCSRFInterceptor } from './csrf';

const API_URL = process.env.VUE_APP_API_URL;

class ApiError extends Error {
  constructor(message, response, config) {
    super(message);
    this.name = 'ApiError';
    this.response = response;
    this.config = config;
    this.status = response?.status;
    this.code = response?.status || 'UNKNOWN';
  }
}

const api = axios.create({
  baseURL: API_URL + '/api',
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000
});

let retryAttempts = 0;
const MAX_RETRY_ATTEMPTS = 3;

// Request interceptor
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    const apiKey = localStorage.getItem('apiKey');

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    if (apiKey) {
      config.headers['X-API-Key'] = apiKey;
    }

    config.metadata = { startTime: Date.now() };

    return config;
  },
  error => {
    logError(error, {
      type: 'request_interceptor_error',
      stage: 'request_setup'
    });
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  response => {
    if (response.config.metadata) {
      const duration = Date.now() - response.config.metadata.startTime;
      if (duration > 5000) {
        logError(new Error('Slow API response'), {
          type: 'performance_warning',
          duration,
          url: response.config.url,
          method: response.config.method
        });
      }
    }
    retryAttempts = 0;
    return response;
  },
  async error => {
    const originalRequest = error.config;

    const apiError = new ApiError(
      error.message || 'API request failed',
      error.response,
      error.config
    );

    const errorContext = {
      url: originalRequest?.url,
      method: originalRequest?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: error.response?.data,
      headers: error.response?.headers,
      duration: originalRequest?.metadata ? Date.now() - originalRequest.metadata.startTime : null
    };

    if (!error.response) {
      if (error.code === 'ECONNABORTED') {
        apiError.message = 'Request timed out. Please try again.';
        errorContext.type = 'timeout_error';
      } else if (error.message === 'Network Error') {
        apiError.message = 'Network error. Please check your connection.';
        errorContext.type = 'network_error';
      } else {
        errorContext.type = 'connection_error';
      }
    } else {
      errorContext.type = 'http_error';

      switch (error.response.status) {
        case 400:
          apiError.message = error.response.data?.message || 'Invalid request. Please check your input.';
          break;
        case 401:
          apiError.message = 'Authentication failed. Please log in again.';
          localStorage.removeItem('token');
          localStorage.removeItem('apiKey');
          setTimeout(() => {
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }, 100);
          break;
        case 403:
          apiError.message = 'Access denied. You do not have permission to perform this action.';
          break;
        case 404:
          apiError.message = 'Resource not found.';
          break;
        case 409:
          apiError.message = error.response.data?.message || 'Conflict. The resource already exists or there is a data conflict.';
          break;
        case 422:
          apiError.message = error.response.data?.message || 'Validation error. Please check your input.';
          break;
        case 429:
          apiError.message = 'Too many requests. Please wait a moment and try again.';
          break;
        case 500:
          apiError.message = 'Internal server error. Please try again later.';
          break;
        case 502:
        case 503:
        case 504:
          apiError.message = 'Service temporarily unavailable. Please try again later.';
          break;
        default:
          apiError.message = error.response.data?.message || `HTTP ${error.response.status}: ${error.response.statusText}`;
      }
    }

    const shouldRetry = (
      !originalRequest._retry &&
      retryAttempts < MAX_RETRY_ATTEMPTS &&
      (
        !error.response ||
        error.response.status >= 500 ||
        error.code === 'ECONNABORTED' ||
        error.message === 'Network Error'
      )
    );

    if (shouldRetry) {
      originalRequest._retry = true;
      retryAttempts++;

      const delay = Math.min(1000 * Math.pow(2, retryAttempts - 1), 5000);

      logError(apiError, {
        ...errorContext,
        retryAttempt: retryAttempts,
        retryDelay: delay,
        willRetry: true
      });

      await new Promise(resolve => setTimeout(resolve, delay));
      return api(originalRequest);
    }

    retryAttempts = 0;

    logError(apiError, {
      ...errorContext,
      willRetry: false,
      finalError: true
    });

    return Promise.reject(apiError);
  }
);

// Set up CSRF token handling
setupCSRFInterceptor(api);

export const createApiMethod = (method, url, options = {}) => {
  return async (data, config = {}) => {
    const context = {
      method,
      url,
      component: options.component || 'unknown',
      ...options.context
    };

    try {
      const response = await withRetry(
        () => api[method](url, data, { ...config, ...options.config }),
        options.maxRetries || 3,
        context
      );
      return response;
    } catch (error) {
      throw error;
    }
  };
};

export const handleApiError = (error, context = {}) => {
  logError(error, {
    type: 'api_error_handler',
    ...context
  });

  if (error instanceof ApiError) {
    return {
      success: false,
      error: error.message,
      status: error.status,
      code: error.code
    };
  }

  return {
    success: false,
    error: error.message || 'An unexpected error occurred',
    status: null,
    code: 'UNKNOWN'
  };
};

export default api;